import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  ColumnMode,
  FillSource,
  ProposalSort,
} from '@/features/reports/lib/order-proposal-math'
import {
  DEFAULT_COLUMN_1,
  DEFAULT_SORT,
} from '@/features/reports/lib/order-proposal-math'

/**
 * The Bestellvorschlag choices each user had last, per browser (CHG-2026-10-bestellvorschlag,
 * D11): the date column, Hauptgruppe, the Tagesgerichte switch, the fill settings, and the sort and
 * "Nullzeilen ausblenden" (CHG-2026-10-bestellvorschlag-simplify, D6). The Liefertag (the order
 * days) is not kept — the report always opens on the next weekday — and neither is the Standort,
 * which the shared report location store seeds.
 *
 * One entry per user, keyed like the Buchhaltung Ansicht (`accountingUserKey`), so two people on
 * one browser each get their own.
 */
export interface OrderProposalPrefs {
  column1: ColumnMode
  mainCategoryId: number | null
  showTagesgerichte: boolean
  /** Only used for a range; a single day always fills from Verkauf. */
  fillSource: FillSource
  upliftPercent: number
  sort: ProposalSort
  hideZeroRows: boolean
}

export const DEFAULT_ORDER_PROPOSAL_PREFS: OrderProposalPrefs = {
  column1: DEFAULT_COLUMN_1,
  mainCategoryId: null,
  showTagesgerichte: true,
  fillSource: 'sum',
  upliftPercent: 20,
  sort: DEFAULT_SORT,
  hideZeroRows: true,
}

interface OrderProposalState {
  byUser: Partial<Record<string, Partial<OrderProposalPrefs>>>
  setPrefs: (userKey: string, prefs: Partial<OrderProposalPrefs>) => void
}

export const useOrderProposalStore = create<OrderProposalState>()(
  persist(
    (set) => ({
      byUser: {},
      setPrefs: (userKey, prefs) =>
        set((state) => ({
          byUser: {
            ...state.byUser,
            [userKey]: { ...state.byUser[userKey], ...prefs },
          },
        })),
    }),
    {
      name: 'order-proposal',
      version: 2,
      migrate: (persisted, version) =>
        version < 2 ? migrateV1(persisted) : (persisted as OrderProposalState),
    },
  ),
)

/** A user's choices with the defaults filled in. */
export function prefsOf(
  byUser: OrderProposalState['byUser'],
  userKey: string,
): OrderProposalPrefs {
  return { ...DEFAULT_ORDER_PROPOSAL_PREFS, ...byUser[userKey] }
}

/**
 * Version 1 had a second date column and fill sources per column: the second column is dropped and
 * every old fill source becomes Σ, which is what "Datum 1" filled from.
 */
function migrateV1(persisted: unknown): OrderProposalState {
  const byUser = (persisted as { byUser?: Record<string, object> } | null)
    ?.byUser
  const migrated: OrderProposalState['byUser'] = {}
  for (const [userKey, prefs] of Object.entries(byUser ?? {})) {
    const {
      column2: _column2,
      fillSource: _fillSource,
      ...rest
    } = prefs as Record<string, unknown>
    migrated[userKey] = {
      ...(rest as Partial<OrderProposalPrefs>),
      fillSource: 'sum',
    }
  }
  return { byUser: migrated } as OrderProposalState
}
